// TODO: Move confirmation interfaces to this file
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

type LimitObject = {
    amount: string;
    currency: string;
};

export type InternalCard = {
    identifier: string;
    name: string;
    type: string;
    disabled: boolean;
    verified: boolean;
    limitRaised: boolean;
    totalSpent: LimitObject;
    limit: Limit;
};

export type Limit = {
    limit: LimitObject | number;
    remainingLimit: LimitObject | number;
    type: string;
    blockType: string;
    subType: 'user_day_limit' | 'non_raised_limit_account_limit' | 'new_limited_account_limit' | 'number_of_non_raised_accounts_limit' | 'account_limit';
    boundaryType: string;
};

export const useLimitsStore = defineStore('limits', () => {
    const cardLimits = ref<InternalCard[]>([]);
    const userLimits = ref<Limit[]>([]);
    const limitedPaymentMethods = ref<string[]>([]);

    function setCardLimits(cards: InternalCard[]): void {
        if (!cards || cards.length === 0) {
            cardLimits.value = [];
            return;
        }

        cardLimits.value = cards.map((card: InternalCard) => {
            return {
                identifier: card.identifier,
                name: card.name,
                type: card.type,
                disabled: card.disabled,
                verified: card.verified,
                limitRaised: card.limitRaised,
                totalSpent: card.totalSpent,
                limit: card.limit
            };
        });
    }

    function setUserLimits(limits: Limit[]): void {
        if (!limits || limits.length === 0) {
            userLimits.value = [];
            return;
        }

        userLimits.value = limits.map((limit: Limit) => {
            return {
                limit: limit.limit,
                remainingLimit: limit.remainingLimit,
                type: limit.type,
                blockType: limit.blockType,
                subType: limit.subType,
                boundaryType: limit.boundaryType
            };
        });
    }

    function setLimitedPaymentMethods(types: string[]): void {
        limitedPaymentMethods.value = types;
    }

    function getNonRaisedCardLimit(): Limit {
        const fallbackLimit = 1000;
        const nonRaisedCardLimit = userLimits.value.find((limit) => limit.subType === 'non_raised_limit_account_limit');

        if (!nonRaisedCardLimit) {
            return {
                limit: {
                    amount: fallbackLimit.toString(),
                    currency: 'EUR'
                },
                remainingLimit: {
                    amount: fallbackLimit.toString(),
                    currency: 'EUR'
                },
                type: 'user',
                blockType: 'hard',
                subType: 'non_raised_limit_account_limit',
                boundaryType: 'maximum'
            };
        }

        return nonRaisedCardLimit;
    }

    function getLimitRaisedCards(): InternalCard[] {
        return cardLimits.value.filter((card) => card.limitRaised);
    }

    function getHighestRemainingCardLimit(): number {
        const userDayLimit = userLimits.value.find((limit) => limit.subType === 'user_day_limit');
        const nonRaisedCardLimit = getNonRaisedCardLimit();

        if (cardLimits.value.length === 0) {
            return determineLimitValue(nonRaisedCardLimit.remainingLimit);
        }

        const remainingLimits = cardLimits.value.map((card) => {
            if (!card.limit) {
                // If the card has no limit, return the remaining day limit which is the highest limit
                return determineLimitValue(userDayLimit.remainingLimit);
            }

            return determineLimitValue(card.limit.remainingLimit);
        });

        return Math.max(...remainingLimits);
    }

    function getTotalAmountSpentUsingNonRaisedCards(): number {
        const nonRaisedCardLimit = getNonRaisedCardLimit();

        return determineLimitValue(nonRaisedCardLimit.limit) - determineLimitValue(nonRaisedCardLimit.remainingLimit);
    }

    function getUserDayLimit(): Limit {
        return userLimits.value.find((limit) => limit.subType === 'user_day_limit');
    }

    return {
        cardLimits: computed(() => cardLimits),
        userLimits: computed(() => userLimits),
        limitedPaymentMethods: computed(() => limitedPaymentMethods),
        getNonRaisedCardLimit: computed(() => getNonRaisedCardLimit),
        getLimitRaisedCards: computed(() => getLimitRaisedCards),
        getHighestRemainingCardLimit: computed(() => getHighestRemainingCardLimit),
        getTotalAmountSpentUsingNonRaisedCards: computed(() => getTotalAmountSpentUsingNonRaisedCards),
        getUserDayLimit: computed(() => getUserDayLimit),
        setCardLimits,
        setUserLimits,
        setLimitedPaymentMethods,
        userDayLimit: computed(() => userLimits.value.find((limit) => limit.subType === 'user_day_limit')),
    };
});

export const determineLimitValue = (limit: LimitObject | number): number => {
    if (!limit) return 0;

    if (typeof limit === 'number') {
        return limit;
    }

    return parseFloat(limit.amount);
};
